import type {Inlet, Outlet} from "#template/runtime-graph/slots"
import {NotReady} from "#template/runtime-graph/slots"
import type {HashedMaterialNodeWithColorOutput} from "#template/runtime-graph/type-descriptors"
import {TypeDescriptors} from "#template/runtime-graph/type-descriptors"
import type {NodeClassImpl} from "#template/runtime-graph/types"
import type {InletDescriptor, OutletDescriptor} from "#template/runtime-graph/descriptors"
import type {ISceneManager} from "#template/interfaces/scene-manager"
import type {Subscription} from "rxjs"
import {defer, map} from "rxjs"
import {hashObject, type TypedImageData} from "@cm/utils"

const TD = TypeDescriptors

export type BakedTextureData = {
    imageData: TypedImageData
    contentHash: string
}

type BakeTextureDescriptorType = {
    sceneManager: InletDescriptor<ISceneManager>
    textureNode: InletDescriptor<HashedMaterialNodeWithColorOutput>
    widthUV: InletDescriptor<number>
    heightUV: InletDescriptor<number>
    widthPx: InletDescriptor<number>
    heightPx: InletDescriptor<number>
    bakedTextureData: OutletDescriptor<BakedTextureData>
}

const bakeTextureDescriptor: BakeTextureDescriptorType = {
    sceneManager: TD.inlet(TD.Identity<ISceneManager>()),
    textureNode: TD.inlet(TD.MaterialNodeWithColorOutput),
    widthUV: TD.inlet(TD.Number),
    heightUV: TD.inlet(TD.Number),
    widthPx: TD.inlet(TD.Number),
    heightPx: TD.inlet(TD.Number),
    bakedTextureData: TD.outlet(TD.Identity<BakedTextureData>("contentHash")),
}

export class BakeTexture implements NodeClassImpl<BakeTextureDescriptorType, typeof BakeTexture> {
    static descriptor = bakeTextureDescriptor
    static uniqueName = "BakeTexture"
    sceneManager!: Inlet<ISceneManager>
    textureNode!: Inlet<HashedMaterialNodeWithColorOutput>
    widthUV!: Inlet<number>
    heightUV!: Inlet<number>
    widthPx!: Inlet<number>
    heightPx!: Inlet<number>
    bakedTextureData!: Outlet<BakedTextureData>

    private pending: Subscription | null = null

    run() {
        this.pending?.unsubscribe()

        if (
            this.sceneManager === NotReady ||
            this.textureNode === NotReady ||
            this.widthUV === NotReady ||
            this.heightUV === NotReady ||
            this.widthPx === NotReady ||
            this.heightPx === NotReady
        ) {
            this.bakedTextureData.emitIfChanged(NotReady)
            return
        }

        const {textureNode, widthUV, heightUV, widthPx, heightPx, sceneManager} = this

        this.pending = sceneManager.addTask({
            description: `bakeTexture(${textureNode.contentHash}:${widthUV}(uv)x${heightUV}(uv):${widthPx}(px)x${heightPx}(px))`,
            task: defer(() =>
                (async () => ({
                    imageData: await sceneManager.bakeTexture(textureNode, widthUV, heightUV, widthPx, heightPx),
                    contentHash: hashObject({
                        type: "BakeTexture",
                        textureNodeHash: textureNode.contentHash,
                        widthUV,
                        heightUV,
                        widthPx,
                        heightPx,
                    }),
                }))(),
            ).pipe(
                map((data) => {
                    this.pending = null
                    this.bakedTextureData.emitIfChanged(data)
                }),
            ),
            critical: true,
        })
    }
}
